import React, { useEffect, useMemo, useState } from 'react';
import { Flex } from 'antd';
import { saveAs } from 'file-saver';
import { keyBy } from 'lodash-es';
import { DateTime } from 'luxon';
import { useParams } from 'react-router-dom';
import * as XLSX from 'xlsx';

import { trackEvent } from 'jf/analytics/Analytics';
import { NumericScore, Study, StudyClient, StudyScoreClient } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { useJFOverlay } from 'jf/common/useJFOverlay';
import { CSVTable } from 'jf/components/CSVTable';
import { DevExButton } from 'jf/components/DevExButton';
import { DevExCheckbox } from 'jf/components/DevExCheckbox';
import { DevExModal } from 'jf/components/DevExModal';
import { DevExSegmented } from 'jf/components/DevExSegmented';
import { DevExText } from 'jf/components/DevExText';
import { JSONBlock } from 'jf/components/JSONBlock';
import { useClientQuery } from 'jf/utils/useClientQuery';

import { useExportableStudyOpenEndedText } from './useExportableStudyOpenEndedText';
import { useExportableStudyScores } from './useExportableStudyScores';
import { useExportableStudyVerbatims } from './useExportableStudyVerbatims';

enum FileType {
  CSV = 'CSV',
  JSON = 'JSON',
}

enum DataType {
  SCORES = 'SCORES',
  VERBATIMS = 'VERBATIMS',
  OPEN_ENDED_TEXT = 'OPEN_ENDED_TEXT',
}

const DATA_TYPE_OPTIONS = [
  { value: DataType.SCORES, label: 'Scores' },
  { value: DataType.VERBATIMS, label: 'Comments' },
  { value: DataType.OPEN_ENDED_TEXT, label: 'Open-Ended Text' },
];

const SCORE_TYPE_OPTIONS = [
  { value: NumericScore.type.TOPIC, label: 'Topic' },
  { value: NumericScore.type.PROMPT, label: 'Prompt' },
];

const SEGMENTED_OPTIONS = [
  { value: 'overall', label: 'Overall' },
  { value: 'segmented', label: 'By Segment' },
];

export const exportCSV = (csvData: string[][], fileNameParts: string[]) => {
  const fileName = fileNameParts.join(' ');
  const sheet = XLSX.utils.aoa_to_sheet(csvData);
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet);
  const buffer = XLSX.write(book, { bookType: 'csv', type: 'buffer' });
  const blob = new Blob([buffer], {
    type: 'text/csv;charset=utf-8',
  });
  saveAs(blob, fileName);
};

export const ExportStudyModal: React.FC = () => {
  const modal = useJFOverlay(ExportStudyModal);
  const { studyRef: studyRefParam, teamRef } = useParams<{ studyRef: string; teamRef: string }>();

  const [dataType, setDataType] = useState<DataType>(DataType.SCORES);
  const [scoreType, setScoreType] = useState<NumericScore.type>(NumericScore.type.TOPIC);
  const [segmented, setSegmented] = useState(false);
  const [includeScores, setIncludeScores] = useState(false);

  const [fileType, setFileType] = useState<FileType>(FileType.CSV);

  const theme = useDevExTheme();

  const { data: study } = useClientQuery(StudyClient.getStudy, { studyRef: studyRefParam });
  const { data: scoredTeams } = useClientQuery(StudyScoreClient.getStudyScoredTeams, {
    studyRef: studyRefParam,
  });

  // avoid fetching score data for active study
  const studyRef = study?.status === Study.status.CLOSED ? study.ref : undefined;

  const exportableScores = useExportableStudyScores({
    studyRef,
    teamRef,
    scoreType,
    segmented,
    includeScores,
  });
  const exportableVerbatims = useExportableStudyVerbatims({ studyRef, teamRef });
  const exportableOpenEndedText = useExportableStudyOpenEndedText({ studyRef, teamRef });

  let exportable;
  if (dataType === DataType.SCORES) {
    exportable = exportableScores;
  } else if (dataType === DataType.OPEN_ENDED_TEXT) {
    exportable = exportableOpenEndedText;
  } else {
    exportable = exportableVerbatims;
  }
  const { csv, json } = exportable;

  const onCancel = () => modal.close();

  // compile Segment event data
  let eventData: Record<string, any> = {
    surveyRef: studyRef,
    fileType,
    dataType: dataType,
  };
  if (dataType === DataType.SCORES) {
    eventData = { ...eventData, type: scoreType, segmented, includeScores };
  }

  const onOk = () => {
    // compose file name
    const teamByRef = keyBy(scoredTeams, 'ref');
    let dataTypeLabel: string;
    if (dataType === DataType.SCORES) {
      dataTypeLabel = `${segmented ? 'Segmented ' : ''}Scores`;
    } else if (dataType === DataType.OPEN_ENDED_TEXT) {
      dataTypeLabel = 'Open-Ended Text';
    } else {
      dataTypeLabel = 'Comments';
    }
    const fileNameParts = [
      study!.name,
      teamRef ? `Team ${teamByRef[teamRef]?.value}` : 'Company',
      dataTypeLabel,
      DateTime.now().toLocaleString(DateTime.DATE_SHORT).replaceAll('/', '-'),
    ];
    const fileName = fileNameParts.join(' ');

    trackEvent('survey-analyzer:export:download', eventData);

    if (fileType === FileType.CSV && csv) {
      exportCSV(csv, fileNameParts);
    } else if (fileType === FileType.JSON && json) {
      // write json to a blob
      const blob = new Blob([JSON.stringify(json, null, 4)], {
        type: 'application/json',
      });

      // save blob as file
      saveAs(blob, fileName);
    }
  };

  // reset state on close
  useEffect(() => {
    setScoreType(NumericScore.type.TOPIC);
    setSegmented(false);
    setIncludeScores(false);
    setFileType(FileType.CSV);
  }, [modal.isOpened]);

  let copyValue = '';
  if (fileType === FileType.CSV && csv) {
    copyValue = csv.map((row) => row.join('\t')).join('\n');
  } else if (fileType === FileType.JSON && json) {
    copyValue = JSON.stringify(json, null, 4);
  }

  const dataTypeOptions = useMemo(() => {
    const containsData = {
      [DataType.SCORES]: (exportableScores.json?.length ?? 0) > 0,
      [DataType.VERBATIMS]: (exportableVerbatims.json?.length ?? 0) > 0,
      [DataType.OPEN_ENDED_TEXT]: (exportableOpenEndedText.json?.length ?? 0) > 0,
    };
    return DATA_TYPE_OPTIONS.map((option) => ({
      ...option,
      disabled: !containsData[option.value],
      title: !containsData[option.value] ? 'No data available' : undefined,
    }));
  }, [exportableScores.json, exportableVerbatims.json, exportableOpenEndedText.json]);

  return (
    <DevExModal
      open={modal.isOpened}
      onCancel={onCancel}
      onOk={onOk}
      title="Export Survey Data"
      footer={
        <>
          <DevExButton
            copyValue={copyValue}
            onClick={() => trackEvent('survey-analyzer:export:copy-to-clipboard', eventData)}
          >
            Copy to clipboard
          </DevExButton>
          <DevExButton type="primary" onClick={onOk}>
            Download {fileType}
          </DevExButton>
        </>
      }
      width={750}
    >
      <Flex vertical gap={theme.variable.spacing.md}>
        <Flex style={{ width: '100%' }}>
          <DevExSegmented<DataType>
            style={{ width: '100%' }}
            options={dataTypeOptions}
            value={dataType}
            onChange={(type) => {
              setDataType(type);
              // set score toggles back to defaults
              setScoreType(NumericScore.type.TOPIC);
              setSegmented(false);
            }}
          />
        </Flex>
        <Flex justify="space-between" style={{ marginTop: theme.variable.spacing.lg }}>
          <Flex align="baseline" gap={theme.variable.spacing.md}>
            <DevExText type="secondary" strong>
              Preview
            </DevExText>

            {dataType === DataType.SCORES && (
              <Flex gap={theme.variable.spacing.md} align="center">
                <DevExSegmented<NumericScore.type>
                  size="small"
                  options={SCORE_TYPE_OPTIONS}
                  value={scoreType}
                  onChange={setScoreType}
                />
                <DevExSegmented
                  size="small"
                  options={SEGMENTED_OPTIONS}
                  value={segmented ? 'segmented' : 'overall'}
                  onChange={(value) => setSegmented(value === 'segmented')}
                />
                <DevExCheckbox checked={includeScores} onChange={setIncludeScores}>
                  Show response breakdown
                </DevExCheckbox>
              </Flex>
            )}
          </Flex>

          <DevExSegmented
            size="small"
            options={Object.values(FileType).map((value) => ({
              value,
              label: value,
            }))}
            value={fileType}
            onChange={(value: FileType) => setFileType(value)}
          />
        </Flex>

        {fileType === FileType.CSV ? (
          <CSVTable data={csv?.slice(0, 6) ?? []} />
        ) : (
          <JSONBlock object={json} truncate={1} />
        )}
      </Flex>
    </DevExModal>
  );
};
